import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Calendar, Clock, Car, Bike, CheckCircle, AlertCircle, ChevronLeft, ChevronRight, Droplets, Gift, CreditCard, Star, Crown, Award, Loader2, ShieldCheck
} from 'lucide-react';
import SystemPayForm from '../components/SystemPayForm';
import { redirectToSystemPay } from '../lib/systempayRedirect';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const PAYMENTS_ENABLED = process.env.REACT_APP_PAYMENTS_ENABLED !== 'false';

const WASH_FORMULAS = [
  { id: 'velo', name: 'VÉLO', price: 12, duration: '15 min' },
  { id: 'clean', name: 'CLEAN', price: 25, duration: '15 min' },
  { id: 'vip', name: 'VIP', price: 55, duration: '30 min' },
  { id: 'platine', name: 'PLATINE', price: 75, duration: '30 min' },
  { id: 'prestige', name: 'PRESTIGE', price: 115, duration: '45 min' }
];

const VEHICLE_TYPES = [
  { id: 'auto', name: 'Voiture', icon: Car },
  { id: 'moto', name: 'Moto', icon: Bike },
  { id: 'velo', name: 'Vélo', icon: Bike }
];

// Abonnements lavage
const WASH_SUBSCRIPTIONS = [
  { id: 'abo-velo', name: 'VÉLO x12', price: 125, originalPrice: 150, savings: 25, perWash: 10.42, icon: Droplets, color: 'green' },
  { id: 'abo-clean', name: 'CLEAN x12', price: 250, originalPrice: 300, savings: 50, perWash: 20.83, icon: Droplets, color: 'blue' },
  { id: 'abo-vip', name: 'VIP x12', price: 550, originalPrice: 660, savings: 110, perWash: 45.83, icon: Star, color: 'yellow', popular: true },
  { id: 'abo-platine', name: 'PLATINE x12', price: 750, originalPrice: 900, savings: 150, perWash: 62.50, icon: Crown, color: 'purple' },
  { id: 'abo-prestige', name: 'PRESTIGE x12', price: 1150, originalPrice: 1380, savings: 230, perWash: 95.83, icon: Award, color: 'red' },
];

export default function WashBookingPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [selectedFormula, setSelectedFormula] = useState(searchParams.get('formula') || '');
  const [selectedVehicle, setSelectedVehicle] = useState('auto');
  const [vehicleInfo, setVehicleInfo] = useState('');
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [loadingSubscription, setLoadingSubscription] = useState(null);
  const [step, setStep] = useState(1);
  const [formToken, setFormToken] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [krLoaded, setKrLoaded] = useState(false);
  const [showSubscriptions, setShowSubscriptions] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('online'); // 'online' or 'onsite'

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // SystemPay success handlers
  const handleWashPaid = useCallback(async () => {
    try {
      await fetch(`${API}/payment/wash/confirm?booking_id=${bookingId}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) { console.error(e); }
    toast.success('Paiement reussi ! RDV confirme');
    navigate('/mon-compte');
  }, [bookingId, token, navigate]);

  const handleSubscriptionPaid = useCallback(async () => {
    try {
      await fetch(`${API}/subscriptions/confirm?subscription_id=${bookingId}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) { console.error(e); }
    toast.success('Abonnement active !');
    navigate('/mon-compte');
  }, [bookingId, token, navigate]);

  const handlePayFailed = useCallback(() => {
    toast.error('Le paiement a echoue');
  }, []);

  // Handle subscription purchase
  const handleBuySubscription = async (planId, planName, price) => {
    if (!user) {
      toast.error('Veuillez vous connecter pour acheter');
      navigate('/connexion');
      return;
    }

    setLoadingSubscription(planId);
    try {
      const res = await fetch(`${API}/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_id: planId,
          plan_name: planName,
          price: price,
          origin_url: window.location.origin
        })
      });

      if (!res.ok) throw new Error('Erreur lors de la création du paiement');
      const data = await res.json();
      if (data.skip_payment) {
        toast.success('Demande enregistree ! A regler en magasin.');
        navigate('/mon-compte');
        return;
      }
      setFormToken(data.formToken);
      setBookingId(data.subscriptionId);
      setStep(5);
    } catch (error) {
      toast.error('Erreur lors de la création du paiement');
    } finally {
      setLoadingSubscription(null);
    }
  };

  // Generate today + next 14 days
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      // Skip Sundays (0)
      if (date.getDay() !== 0) {
        dates.push({
          value: date.toISOString().split('T')[0],
          label: i === 0 ? "Aujourd'hui" : date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }),
          dayName: date.toLocaleDateString('fr-FR', { weekday: 'long' })
        });
      }
    }
    return dates;
  };

  const availableDates = getAvailableDates();

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const fetchAvailableSlots = async () => {
    setLoadingSlots(true);
    try {
      const response = await axios.get(`${API}/wash/available-slots?date=${selectedDate}`);
      let slots = response.data.available_slots || [];
      // If the selected date is today, filter out past time slots (give 30min buffer for prep)
      const todayStr = new Date().toISOString().split('T')[0];
      if (selectedDate === todayStr) {
        const now = new Date();
        const cutoff = now.getHours() * 60 + now.getMinutes() + 30; // 30 minutes buffer
        slots = slots.filter((s) => {
          const [h, m] = s.split(':').map(Number);
          return (h * 60 + m) >= cutoff;
        });
      }
      setAvailableSlots(slots);
    } catch (error) {
      toast.error('Erreur lors du chargement des créneaux');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSubmit = async (e, paymentMode = 'full') => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Veuillez vous connecter pour réserver');
      navigate('/connexion');
      return;
    }

    if (!selectedDate || !selectedSlot || !selectedFormula) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      // Create booking with SystemPay payment
      const res = await fetch(`${API}/payment/wash/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          date: selectedDate,
          time_slot: selectedSlot,
          wash_type: selectedFormula,
          vehicle_type: selectedVehicle,
          vehicle_info: `${vehiclePlate}${vehicleInfo ? ' - ' + vehicleInfo : ''}`,
          notes: notes,
          payment_mode: paymentMode,
          origin_url: window.location.origin
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Erreur lors de la réservation');
      }

      const data = await res.json();
      if (data.skip_payment) {
        // RDV cree directement - paiement sur place
        toast.success('Reservation confirmee ! A regler en magasin.');
        navigate('/mon-compte');
        return;
      }
      if (data.redirect_form) {
        redirectToSystemPay(data.redirect_form);
        return;
      }
      setFormToken(data.formToken);
      setBookingId(data.bookingId);
      setStep(4);
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la réservation');
    } finally {
      setLoading(false);
    }
  };

  // Group slots by hour for better display
  const groupedSlots = availableSlots.reduce((acc, slot) => {
    const hour = slot.split(':')[0];
    if (!acc[hour]) acc[hour] = [];
    acc[hour].push(slot);
    return acc;
  }, {});

  // Get sorted hours
  const sortedHours = Object.keys(groupedSlots).sort((a, b) => parseInt(a) - parseInt(b));

  const selectedFormulaData = WASH_FORMULAS.find(f => f.id === selectedFormula);

  return (
    <div className="min-h-screen bg-[#09090B] py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-blue-500/10 text-blue-400 px-4 py-2 rounded-full mb-4">
            <Droplets size={18} />
            <span className="text-sm font-medium">Réservation Lavage</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Réservez votre créneau
          </h1>
          <p className="text-zinc-400">
            Créneaux de 15 minutes • 4 véhicules par heure maximum
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                  ${step >= s ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-500'}`}>
                  {s}
                </div>
                {s < 3 && (
                  <div className={`w-12 h-1 ${step > s ? 'bg-red-600' : 'bg-zinc-800'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Choose Formula & Vehicle */}
        {step === 1 && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <span className="bg-red-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">1</span>
                Choisissez votre formule et véhicule
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Vehicle Type */}
              <div>
                <Label className="text-zinc-300 mb-3 block">Type de véhicule</Label>
                <div className="grid grid-cols-3 gap-3">
                  {VEHICLE_TYPES.map((v) => (
                    <Button
                      key={v.id}
                      type="button"
                      variant={selectedVehicle === v.id ? 'default' : 'outline'}
                      className={selectedVehicle === v.id ? 'bg-red-600 hover:bg-red-700' : 'border-zinc-700'}
                      onClick={() => {
                        setSelectedVehicle(v.id);
                        if (v.id === 'velo') setSelectedFormula('velo');
                      }}
                    >
                      <v.icon size={18} className="mr-2" />
                      {v.name}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Formula Selection */}
              <div>
                <Label className="text-zinc-300 mb-3 block">Formule de lavage</Label>
                <div className="grid md:grid-cols-2 gap-3">
                  {WASH_FORMULAS.filter(f => 
                    selectedVehicle === 'velo' ? f.id === 'velo' : f.id !== 'velo'
                  ).map((formula) => (
                    <div
                      key={formula.id}
                      onClick={() => setSelectedFormula(formula.id)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all
                        ${selectedFormula === formula.id 
                          ? 'border-red-500 bg-red-500/10' 
                          : 'border-zinc-700 hover:border-zinc-600'}`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="text-white font-bold">{formula.name}</h3>
                          <p className="text-zinc-500 text-sm">{formula.duration}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-2xl font-bold text-white">{formula.price}€</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={() => setStep(2)}
                  disabled={!selectedFormula}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="wash-step1-continue"
                >
                  Continuer <ChevronRight size={18} className="ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Choose Date & Time */}
        {step === 2 && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <span className="bg-red-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">2</span>
                Choisissez la date et l'heure
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Date Selection */}
              <div>
                <Label className="text-zinc-300 mb-3 block">
                  <Calendar size={16} className="inline mr-2" />
                  Date
                </Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {availableDates.slice(0, 8).map((date) => (
                    <Button
                      key={date.value}
                      type="button"
                      variant={selectedDate === date.value ? 'default' : 'outline'}
                      className={`h-auto py-2 ${selectedDate === date.value ? 'bg-red-600 hover:bg-red-700' : 'border-zinc-700'}`}
                      onClick={() => { setSelectedDate(date.value); setSelectedSlot(''); }}
                    >
                      <div className="text-center">
                        <div className="text-xs opacity-70">{date.dayName}</div>
                        <div className="font-bold">
                          {date.label === "Aujourd'hui"
                            ? "Aujourd'hui"
                            : date.label.split(' ').slice(1).join(' ')}
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div>
                  <Label className="text-zinc-300 mb-3 block">
                    <Clock size={16} className="inline mr-2" />
                    Créneau horaire
                  </Label>
                  
                  {loadingSlots ? (
                    <div className="text-center py-8 text-zinc-500">Chargement des créneaux...</div>
                  ) : availableSlots.length === 0 ? (
                    <div className="text-center py-8">
                      <AlertCircle className="mx-auto text-yellow-500 mb-2" size={32} />
                      <p className="text-zinc-400">Aucun créneau disponible ce jour</p>
                      <p className="text-zinc-500 text-sm">Essayez une autre date</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {sortedHours.map((hour) => (
                        <div key={hour}>
                          <p className="text-zinc-500 text-sm mb-2">{hour}h</p>
                          <div className="grid grid-cols-4 gap-2">
                            {groupedSlots[hour].sort().map((slot) => (
                              <Button
                                key={slot}
                                type="button"
                                variant={selectedSlot === slot ? 'default' : 'outline'}
                                size="sm"
                                className={selectedSlot === slot ? 'bg-red-600 hover:bg-red-700' : 'border-zinc-700'}
                                onClick={() => setSelectedSlot(slot)}
                              >
                                {slot}
                              </Button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)} className="border-zinc-700">
                  <ChevronLeft size={18} className="mr-1" /> Retour
                </Button>
                <Button 
                  onClick={() => setStep(3)}
                  disabled={!selectedDate || !selectedSlot}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="wash-step2-continue"
                >
                  Continuer <ChevronRight size={18} className="ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Confirm */}
        {step === 3 && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <span className="bg-red-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">3</span>
                Confirmez votre réservation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Summary */}
                <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                  <h3 className="text-white font-medium mb-3">Récapitulatif</h3>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Formule</span>
                    <span className="text-white font-medium">{selectedFormulaData?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Véhicule</span>
                    <span className="text-white">{VEHICLE_TYPES.find(v => v.id === selectedVehicle)?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Date</span>
                    <span className="text-white">
                      {new Date(selectedDate).toLocaleDateString('fr-FR', { 
                        weekday: 'long', day: 'numeric', month: 'long' 
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Heure</span>
                    <span className="text-white font-bold">{selectedSlot}</span>
                  </div>
                  <hr className="border-zinc-700" />
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Prix</span>
                    <span className="text-2xl font-bold text-red-500">{selectedFormulaData?.price}€</span>
                  </div>
                </div>

                {/* Vehicule plaque - OBLIGATOIRE */}
                <div>
                  <Label className="text-zinc-300">Plaque d'immatriculation <span className="text-red-500">*</span></Label>
                  <Input
                    required
                    value={vehiclePlate}
                    onChange={(e) => setVehiclePlate(e.target.value.toUpperCase())}
                    placeholder="AB-123-CD"
                    className="bg-zinc-800 border-zinc-700 text-white mt-1 uppercase"
                    data-testid="wash-plate"
                  />
                </div>

                {/* Additional Info */}
                <div>
                  <Label className="text-zinc-300">Informations vehicule (marque/modele)</Label>
                  <Input
                    value={vehicleInfo}
                    onChange={(e) => setVehicleInfo(e.target.value)}
                    placeholder="Ex: Peugeot 308 grise"
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>

                <div>
                  <Label className="text-zinc-300">Notes (optionnel)</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Instructions particulières..."
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    rows={2}
                  />
                </div>

                {!user && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <p className="text-yellow-400 text-sm">
                      <AlertCircle size={16} className="inline mr-2" />
                      Vous devez être connecté pour réserver. 
                      <Button 
                        variant="link" 
                        className="text-yellow-400 p-0 ml-1"
                        onClick={() => navigate('/connexion')}
                      >
                        Se connecter
                      </Button>
                    </p>
                  </div>
                )}

                {/* Payment info */}
                <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/30">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="text-green-400" size={24} />
                    <div>
                      <p className="text-green-400 font-bold">
                        {PAYMENTS_ENABLED ? 'Paiement en ligne sécurisé' : 'Paiement en magasin'}
                      </p>
                      <p className="text-zinc-500 text-sm">
                        {PAYMENTS_ENABLED ? 'Paiement par carte bancaire via SystemPay' : 'Vous reglez sur place lors du RDV'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row md:justify-between gap-3">
                  <Button variant="outline" type="button" onClick={() => setStep(2)} className="border-zinc-700">
                    <ChevronLeft size={18} className="mr-1" /> Retour
                  </Button>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button 
                      type="button"
                      onClick={(e) => handleSubmit(e, 'deposit')}
                      disabled={loading || !user}
                      variant="outline"
                      className="border-orange-500 text-orange-400 hover:bg-orange-500/10"
                      data-testid="wash-submit-deposit"
                    >
                      <CreditCard size={18} className="mr-2" />
                      {loading
                        ? <><Loader2 className="animate-spin mr-1" size={16} /> ...</>
                        : `Acompte 5€ (reste ${(selectedFormulaData?.price || 0) - 5}€ en magasin)`}
                    </Button>
                    <Button 
                      type="button"
                      onClick={(e) => handleSubmit(e, 'full')}
                      disabled={loading || !user}
                      className="bg-red-600 hover:bg-red-700"
                      data-testid="wash-submit-pay"
                    >
                      <CreditCard size={18} className="mr-2" />
                      {loading
                        ? <><Loader2 className="animate-spin mr-1" size={16} /> Préparation...</>
                        : `Payer ${selectedFormulaData?.price}€`}
                    </Button>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 4: SystemPay Payment */}
        {step === 4 && formToken && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="text-red-500" size={20} />
                Paiement sécurisé - {selectedFormulaData?.name} ({selectedFormulaData?.price}€)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4 bg-zinc-800/50 rounded-lg p-4">
                <div className="flex justify-between text-sm text-zinc-400 mb-1">
                  <span>Formule</span><span className="text-white font-bold">{selectedFormulaData?.name}</span>
                </div>
                <div className="flex justify-between text-sm text-zinc-400 mb-1">
                  <span>Date</span><span className="text-white">{selectedDate && new Date(selectedDate + 'T00:00:00').toLocaleDateString('fr-FR', {weekday: 'long', day: 'numeric', month: 'long'})}</span>
                </div>
                <div className="flex justify-between text-sm text-zinc-400 mb-1">
                  <span>Heure</span><span className="text-white font-bold">{selectedSlot}</span>
                </div>
                <div className="flex justify-between text-sm text-zinc-400 pt-2 border-t border-zinc-700 mt-2">
                  <span className="font-bold">Total</span><span className="text-red-500 font-bold text-lg">{selectedFormulaData?.price}€</span>
                </div>
              </div>

              <SystemPayForm
                formToken={formToken}
                amount={selectedFormulaData?.price}
                onPaid={handleWashPaid}
                onFailed={handlePayFailed}
                testId="wash-systempay-form"
              />

              <div className="mt-4">
                <Button variant="outline" onClick={() => { setStep(3); setFormToken(null); }} className="border-zinc-700">
                  <ChevronLeft size={18} className="mr-1" /> Retour
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Subscription Payment */}
        {step === 5 && formToken && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="text-green-500" size={20} />
                Paiement de l'abonnement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SystemPayForm 
                formToken={formToken} 
                amount={selectedSubscription?.price || selectedFormulaData?.price}
                onPaid={handleSubscriptionPaid}
                onFailed={handlePayFailed}
                testId="wash-sub-systempay-form"
              />
              <div className="mt-4">
                <Button variant="outline" onClick={() => { setStep(1); setFormToken(null); setShowSubscriptions(true); }} className="border-zinc-700">
                  <ChevronLeft size={18} className="mr-1" /> Retour
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info */}
        <div className="mt-8 text-center text-zinc-500 text-sm">
          <p>Paiement sur place • Annulation gratuite jusqu'à 2h avant</p>
          <p className="mt-1">Horaires: 8h30-19h00 (fermé dimanche)</p>
        </div>

        {/* Subscriptions Section */}
        <div className="mt-12">
          <div 
            onClick={() => setShowSubscriptions(!showSubscriptions)}
            className="bg-gradient-to-r from-green-900/30 via-green-600/20 to-green-900/30 rounded-xl p-6 border border-green-500/30 cursor-pointer hover:border-green-500/50 transition-all"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Gift className="text-green-400" size={40} />
                <div>
                  <h3 className="text-xl font-bold text-white">FORMULES ABONNEMENT</h3>
                  <p className="text-zinc-400">10 lavages achetés = 2 OFFERTS • Paiement en ligne sécurisé</p>
                </div>
              </div>
              <Button className="bg-green-600 hover:bg-green-700">
                <CreditCard size={20} className="mr-2" />
                {showSubscriptions ? 'Masquer' : 'Voir les offres'}
              </Button>
            </div>
          </div>

          {showSubscriptions && (
            <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {WASH_SUBSCRIPTIONS.map((sub) => {
                const colorClasses = {
                  green: 'border-green-500/30 bg-green-600',
                  blue: 'border-blue-500/30 bg-blue-600',
                  yellow: 'border-yellow-500/50 bg-yellow-500 ring-2 ring-yellow-500/30',
                  purple: 'border-purple-500/30 bg-purple-600',
                  red: 'border-red-500/30 bg-red-600'
                };
                const textColors = {
                  green: 'text-green-400',
                  blue: 'text-blue-400',
                  yellow: 'text-yellow-400',
                  purple: 'text-purple-400',
                  red: 'text-red-500'
                };
                
                return (
                  <Card key={sub.id} className={`bg-zinc-900/80 border-zinc-800 ${colorClasses[sub.color].split(' ')[0]} overflow-hidden`}>
                    {sub.popular && (
                      <div className="bg-yellow-500 text-black text-center py-1 text-xs font-bold">
                        ⭐ BEST SELLER
                      </div>
                    )}
                    <CardContent className="p-5 text-center">
                      <sub.icon className={`${textColors[sub.color]} mx-auto mb-2`} size={28} />
                      <h3 className="text-lg font-bold text-white mb-1">{sub.name}</h3>
                      <p className="text-zinc-500 text-xs mb-3">Carte 12 lavages • 2 offerts</p>
                      <div className="mb-3">
                        <span className="text-zinc-500 line-through text-sm">{sub.originalPrice}€</span>
                        <p className={`text-3xl font-bold ${textColors[sub.color]}`}>{sub.price}€</p>
                        <span className="text-zinc-500 text-xs">soit {sub.perWash}€/lavage</span>
                      </div>
                      <div className={`bg-${sub.color}-500/10 rounded-lg p-2 mb-3`}>
                        <p className={`${textColors[sub.color]} font-semibold text-sm`}>
                          Économie : {sub.savings}€
                        </p>
                      </div>
                      <Button 
                        onClick={() => handleBuySubscription(sub.id, sub.name, sub.price)}
                        disabled={loadingSubscription === sub.id}
                        className={`w-full ${colorClasses[sub.color].split(' ').slice(1).join(' ')} hover:opacity-90 ${sub.color === 'yellow' ? 'text-black' : 'text-white'}`}
                      >
                        <CreditCard size={16} className="mr-2" />
                        {loadingSubscription === sub.id ? 'Chargement...' : 'Acheter en ligne'}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
